/**
 * =============================================================================
 * IDS CRM Web - Parts used lines in Log Work (spare parts, Push 2)
 * =============================================================================
 *
 * The "Parts used" block of job Log Work and the ticket's work log window
 * (build spec 9.4): the parts picker on top, then one line per part fitted,
 * each with its quantity, the robot it went on (a job with several robots),
 * who added it and a remove button. Laid out like EngineerPicker, with every
 * control at least 44px for a gloved thumb.
 *
 * The lines belong to the page, never to this component: the job page reads
 * them from saved Log Work (one spare:<uuid> entry per line) and the ticket
 * window from its own state. This component only reports changes:
 *  - onAdd(picked) when a part is picked from the list (or "not in the list");
 *  - onChange(key, value) with the whole new value, only when something
 *    really changed: every save moves the line's version on, and a needless
 *    one makes a colleague's Complete Work Log fail with "changed after your
 *    screen loaded";
 *  - onRemove(key);
 *  - onQuantityProblem(key, sentence | null) while a quantity box holds text
 *    that cannot be read, so Save can stop on it instead of filing the old
 *    quantity silently.
 *
 * No prices, ever: engineers never see a cost or a charge.
 * =============================================================================
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import SparePartsPicker from './SparePartsPicker';
import { ukDayLabelOf, firstNameOf } from './SavedLogWorkPanel';
import {
  normaliseQuantityText,
  parseQuantity,
  quantityText,
  quantityBoxProblem,
  spareRobotGone,
  spareValueProblem,
  QUANTITY_HINT
} from '../utils/spareLines';
import './SparePartsUsed.css';

const WHOLE_JOB = '__whole_job__';
const QUANTITY_IDLE_MS = 600;

const lower = (value) => String(value ?? '').trim().toLowerCase();

const without = (map, key) => {
  if (!(key in map)) return map;
  const next = { ...map };
  delete next[key];
  return next;
};

const lineName = (value) => (value && typeof value.name === 'string' && value.name.trim()) || 'this part';

// 'Sue' for 'Sue Smith'; a phrase such as 'a colleague' is kept whole.
const firstNameOrPhrase = (name) => {
  const text = String(name || '').trim();
  if (!text) return 'a colleague';
  return /^(a|an|the)\s/i.test(text) ? text : firstNameOf(text);
};

/**
 * @param {object} props
 * @param {string} [props.id] - base id for the controls
 * @param {Array<{key: string, value: object, pending?: boolean, failed?: boolean, conflict?: boolean}>} props.lines
 * @param {Array<{asset_id: string, asset_ref: string}>} [props.robots] - the job's robots ([] on a ticket)
 * @param {{items: Array, status: string}|null} props.catalogue - useSparePartsList()
 * @param {(userId: string) => string} [props.nameOf]
 * @param {boolean} [props.showAddedBy]
 * @param {boolean} [props.disabled]
 * @param {(picked: object) => void} props.onAdd
 * @param {(key: string, value: object) => void} props.onChange
 * @param {(key: string) => void} props.onRemove
 * @param {(key: string, sentence: string|null) => void} [props.onQuantityProblem]
 */
function SparePartsUsed({
  id = 'spare-parts-used',
  lines = [],
  robots = [],
  catalogue = null,
  nameOf,
  showAddedBy = false,
  disabled = false,
  onAdd,
  onChange,
  onRemove,
  onQuantityProblem
}) {
  // What is typed in a quantity box, per line key, while it differs from the
  // saved quantity or cannot be read. Without an entry the box shows the line.
  const [drafts, setDrafts] = useState({});
  const [invalid, setInvalid] = useState({});
  const timers = useRef({});
  const reported = useRef(new Set());
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const problemRef = useRef(onQuantityProblem);
  problemRef.current = onQuantityProblem;

  const jobRobots = useMemo(() => (Array.isArray(robots) ? robots : [])
    .filter(robot => robot && robot.asset_id)
    .map(robot => ({ asset_id: lower(robot.asset_id), asset_ref: robot.asset_ref || 'Robot' })), [robots]);
  const robotIds = useMemo(() => new Set(jobRobots.map(robot => robot.asset_id)), [jobRobots]);
  const severalRobots = jobRobots.length > 1;

  const report = (key, sentence) => {
    if (sentence) {
      reported.current.add(key);
    } else {
      if (!reported.current.has(key)) return;
      reported.current.delete(key);
    }
    if (typeof problemRef.current === 'function') problemRef.current(key, sentence || null);
  };

  // A line that leaves the list (removed here, or by a colleague and gone at
  // the next refresh) takes its quantity problem and typing with it.
  const lineKeys = lines.map(line => line.key).join('|');
  useEffect(() => {
    const present = new Set(linesRef.current.map(line => line.key));
    for (const key of [...reported.current]) {
      if (!present.has(key)) {
        reported.current.delete(key);
        if (typeof problemRef.current === 'function') problemRef.current(key, null);
      }
    }
    for (const key of Object.keys(timers.current)) {
      if (!present.has(key)) {
        clearTimeout(timers.current[key]);
        delete timers.current[key];
      }
    }
    setDrafts(prev => (Object.keys(prev).some(key => !present.has(key))
      ? Object.fromEntries(Object.entries(prev).filter(([key]) => present.has(key)))
      : prev));
    setInvalid(prev => (Object.keys(prev).some(key => !present.has(key))
      ? Object.fromEntries(Object.entries(prev).filter(([key]) => present.has(key)))
      : prev));
  }, [lineKeys]);

  // Leaving the window takes its quantity problems with it, so a page that
  // keeps them never stops a later Save on a box no longer on screen.
  useEffect(() => () => {
    Object.values(timers.current).forEach(timer => clearTimeout(timer));
    timers.current = {};
    for (const key of reported.current) {
      if (typeof problemRef.current === 'function') problemRef.current(key, null);
    }
    reported.current.clear();
  }, []);

  // fromBlur: the engineer has finished with the box (left it, or pressed
  // Enter or Done). Otherwise it is the pause while typing, which saves a
  // readable quantity but never flags one still being typed ('2.' on the way
  // to '2.5', or the box cleared to type a new number): that waits for the
  // box to be left. The page is still told, quietly, so a Save that somehow
  // comes without leaving the box never files the old quantity.
  const commitQuantity = (key, fromBlur) => {
    clearTimeout(timers.current[key]);
    delete timers.current[key];
    const text = draftsRef.current[key];
    if (text === undefined) return; // nothing typed: nothing to save
    const line = linesRef.current.find(entry => entry.key === key);
    if (!line || !line.value) return;
    const parsed = parseQuantity(normaliseQuantityText(text));
    if (!parsed.ok) {
      if (fromBlur) setInvalid(prev => ({ ...prev, [key]: true }));
      report(key, quantityBoxProblem(lineName(line.value)));
      return;
    }
    setInvalid(prev => without(prev, key));
    report(key, null);
    if (quantityText(parsed.text) !== quantityText(line.value.quantity) && typeof onChange === 'function') {
      onChange(key, { ...line.value, quantity: Number(parsed.text) });
    }
    // On leaving the box it shows the line again ('2,5' becomes '2.5').
    // While typing it is left alone, so '2.' can still become '2.5'.
    if (fromBlur) {
      draftsRef.current = without(draftsRef.current, key);
      setDrafts(prev => without(prev, key));
    }
  };

  // Enter (or Done / Go on a phone keyboard) in a quantity box saves the
  // quantity typed and closes the keyboard. It must never submit the Log
  // Work form around it, which would file the quantity from before.
  const quantityKeyDown = (key, event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    commitQuantity(key, true);
    if (event.currentTarget && typeof event.currentTarget.blur === 'function') event.currentTarget.blur();
  };

  const typeQuantity = (key, text) => {
    setDrafts(prev => ({ ...prev, [key]: text }));
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => commitQuantity(key, false), QUANTITY_IDLE_MS);
  };

  const chooseRobot = (line, choice) => {
    const next = choice === WHOLE_JOB
      ? { asset_id: null, whole_job: true }
      : { asset_id: choice || null, whole_job: false };
    const currentAsset = line.value.asset_id ? lower(line.value.asset_id) : null;
    if (currentAsset === next.asset_id && Boolean(line.value.whole_job) === next.whole_job) return;
    if (typeof onChange === 'function') onChange(line.key, { ...line.value, ...next });
  };

  const removeLine = (key) => {
    clearTimeout(timers.current[key]);
    delete timers.current[key];
    setDrafts(prev => without(prev, key));
    setInvalid(prev => without(prev, key));
    report(key, null);
    if (typeof onRemove === 'function') onRemove(key);
  };

  const shown = (Array.isArray(lines) ? lines : []).filter(line => line && line.value);

  return (
    <div className="spare-parts-used">
      <label className="spare-parts-used-label" htmlFor={`${id}-search`}>Add a part</label>
      <SparePartsPicker
        id={`${id}-search`}
        items={catalogue && Array.isArray(catalogue.items) ? catalogue.items : []}
        status={catalogue ? catalogue.status : 'none'}
        disabled={disabled}
        onPick={(picked) => { if (typeof onAdd === 'function') onAdd(picked); }}
      />

      {shown.length === 0 ? (
        <p className="spare-parts-used-empty">No parts added yet.</p>
      ) : (
        <ul className="spare-parts-used-list" aria-label="Parts used">
          {shown.map(line => {
            const { key, value } = line;
            const name = lineName(value);
            const qtyId = `${id}-qty-${key.replace(/[^a-z0-9-]/gi, '')}`;
            const robotGone = spareRobotGone(value, robotIds);
            const problem = robotGone ? null : spareValueProblem(value, { robotIds, severalRobots });
            const robotChoice = value.whole_job ? WHOLE_JOB : (value.asset_id ? lower(value.asset_id) : '');
            const adder = showAddedBy && nameOf ? firstNameOrPhrase(nameOf(value.added_by)) : null;
            const addedDay = value.added_at ? ukDayLabelOf(value.added_at) : '';
            const shownQuantity = drafts[key] !== undefined ? drafts[key] : quantityText(value.quantity);
            return (
              <li key={key} className={`spare-line${invalid[key] || problem || robotGone ? ' has-problem' : ''}`}>
                <div className="spare-line-main">
                  <div className="spare-line-name">
                    <span className="spare-line-part">{value.name}</span>
                    {value.item_number && <span className="spare-line-number">{value.item_number}</span>}
                  </div>
                  {value.not_in_list && (
                    <span className="spare-line-badge">Not in the list – the office will price it</span>
                  )}

                  <div className="spare-line-controls">
                    <label className="spare-line-qty" htmlFor={qtyId}>
                      <span aria-hidden="true">Qty</span>
                      <input
                        id={qtyId}
                        type="text"
                        inputMode="decimal"
                        enterKeyHint="done"
                        autoComplete="off"
                        aria-label={`Quantity of ${name}`}
                        aria-invalid={invalid[key] ? 'true' : undefined}
                        value={shownQuantity}
                        disabled={disabled}
                        onChange={event => typeQuantity(key, event.target.value)}
                        onKeyDown={event => quantityKeyDown(key, event)}
                        onBlur={() => commitQuantity(key, true)}
                      />
                    </label>
                    {(severalRobots || robotGone) && (
                      <select
                        className="spare-line-robot"
                        aria-label={`Which robot for ${name}`}
                        value={robotChoice}
                        disabled={disabled}
                        onChange={event => chooseRobot(line, event.target.value)}
                      >
                        <option value="" disabled>Which robot?</option>
                        {robotGone && (
                          <option value={robotChoice} disabled>A robot no longer on this job</option>
                        )}
                        {jobRobots.map(robot => (
                          <option key={robot.asset_id} value={robot.asset_id}>{robot.asset_ref}</option>
                        ))}
                        <option value={WHOLE_JOB}>Whole job (fixings, cable, consumables)</option>
                      </select>
                    )}
                  </div>

                  {invalid[key] && <p className="spare-line-problem" role="alert">{QUANTITY_HINT}</p>}
                  {robotGone && (
                    <p className="spare-line-problem">This robot is no longer on the job. Choose another robot or Whole job.</p>
                  )}
                  {problem && <p className="spare-line-problem">{problem}</p>}

                  {(adder || line.pending || line.failed || line.conflict) && (
                    <div className="spare-line-meta">
                      {adder && <span>Added by {adder}{addedDay ? ` · ${addedDay}` : ''}</span>}
                      {line.pending && <span className="spare-line-flag">Not sent yet</span>}
                      {line.failed && <span className="spare-line-flag is-failed">Not saved – try again</span>}
                      {line.conflict && <span className="spare-line-flag is-failed">Changed by someone else – choose above</span>}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="spare-line-remove"
                  onClick={() => removeLine(key)}
                  disabled={disabled}
                  aria-label={`Remove ${name}`}
                  title={`Remove ${name}`}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default SparePartsUsed;
